import { add, angle, arcSegmentContent, centerOfArc, dTol, endTangent, intersection, lineSegmentContent, mult, normalize, radius, rotate, rotateCcw, squareDistance, toRadians, vector, } from "./geometry";
import { isNumber, } from "./wsi4_typeguards";
function approximateProfile(part, thickness) {
    const r = 2. * thickness;
    if (r <= 0.) {
        throw Error("Wrong thickness.");
    }
    if (part.outerAngleInRadians === 0.) {
        throw Error("No Profile.");
    }
    const startStart = {
        entries: [
            0.,
            0.,
        ],
    };
    const startEnd = {
        entries: [
            part.flangeLength,
            0.,
        ],
    };
    const arcCenter = centerOfArc(startEnd, {
        entries: [
            1.,
            0.,
        ],
    }, part.outerAngleInRadians > 0., r);
    const arcTo = rotate(startEnd, arcCenter, part.outerAngleInRadians);
    const arc = arcSegmentContent(startEnd, arcTo, arcCenter, part.outerAngleInRadians > 0.);
    if (Math.abs(radius(arc) - r) > dTol) {
        throw Error("approximateProfile: Error constructing.  Radius of arc wrong: " + radius(arc)
            .toString() + " instead of " + r.toString());
    }
    if (Math.abs(angle(arc) - part.outerAngleInRadians) > dTol) {
        throw Error("approximateProfile: Error constructing.  Angle of arc wrong: " + angle(arc)
            .toString() + " instead of " + part.outerAngleInRadians.toString());
    }
    const tangent = endTangent(arc);
    return {
        startSegment: lineSegmentContent(startStart, startEnd),
        arcSegment: arc,
        endSegment: lineSegmentContent(arc.to, add(arc.to, tangent)),
        absArcOuterAngle: Math.abs(part.outerAngleInRadians),
    };
}
function computeFlangeIntersection(first, second) {
    const bigM = 1e6;
    const firstVec = mult(bigM, normalize(vector(first.from, first.to)));
    const secondVec = mult(bigM, normalize(vector(second.from, second.to)));
    const i = intersection({
        from: add(first.from, mult(-1., firstVec)),
        to: add(first.to, firstVec),
    }, {
        from: add(second.from, mult(-1., secondVec)),
        to: add(second.to, secondVec),
    });
    if (i === undefined) {
        throw Error("Wrong segments.");
    }
    return i;
}
function computeArcLength(profile) {
    const flangeEnd = (() => {
        if (profile.absArcOuterAngle <= Math.PI * 0.5) {
            return computeFlangeIntersection(profile.startSegment, profile.endSegment);
        }
        else {
            const direction = normalize(vector(profile.startSegment.from, profile.startSegment.to));
            const pointOnArc = add(profile.arcSegment.center, mult(radius(profile.arcSegment), direction));
            return computeFlangeIntersection(profile.startSegment, {
                from: pointOnArc,
                to: add(pointOnArc, rotateCcw(direction)),
            });
        }
    })();
    return Math.sqrt(squareDistance(flangeEnd, profile.arcSegment.from));
}
function isValidNumber(n) {
    return isNumber(n) && n > 0;
}
function flangeLength(part, lastFlangeLength, thickness) {
    if (!isValidNumber(thickness) || !isValidNumber(part.flangeLength)) {
        return {
            beforeArcLength: 0.,
            afterArcLength: 0.,
            lineLength: part.flangeLength,
        };
    }
    const beforeArcLength = lastFlangeLength !== undefined ? lastFlangeLength.afterArcLength : 0.;
    const afterArcLength = part.outerAngleInRadians !== 0. ? computeArcLength(approximateProfile(part, thickness)) : 0.;
    return {
        beforeArcLength: beforeArcLength,
        afterArcLength: afterArcLength,
        lineLength: part.flangeLength - beforeArcLength - afterArcLength,
    };
}
export function sheetMetalProfileParts(elements, thickness) {
    const arr = [];
    for (const e of elements) {
        const c = {
            flangeLength: e.flangeLength,
            outerAngleInRadians: e.innerAngleInDegrees === 0. ? 0. : (e.ccw ? 1. : -1.) * (toRadians(180. - e.innerAngleInDegrees)),
        };
        arr.push({
            content: c,
            flangeLength: flangeLength(c, arr.length === 0 ? undefined : arr[arr.length - 1].flangeLength, thickness),
        });
    }
    return arr;
}
